import { Alert as ChakraAlert, AlertTitle, CloseButton, Flex, Spacer } from "@chakra-ui/react";
import { Info } from "@airhelp/icons";
import { PropsWithChildren } from "react";

export interface AlertProps {
  isOpen: boolean;
  onClose?: () => void;
  withIcon?: boolean;
  variant: "success" | "danger" | "info" | "action";
  isExperiment?: boolean;
}

export const alertThemes = {
  success: {
    background: "success.100",
    borderColor: "success.200",
    iconColor: "success.500",
    textColor: "primary.900",
    closeButtonColor: "greyscale.600",
  },
  danger: {
    background: "danger.100",
    borderColor: "danger.200",
    iconColor: "danger.500",
    textColor: "primary.900",
    closeButtonColor: "greyscale.600",
  },
  info: {
    background: "primary.100",
    borderColor: "greyscale.400",
    iconColor: "primary.500",
    textColor: "primary.900",
    closeButtonColor: "greyscale.600",
  },
  action: {
    background: "primary.500",
    borderColor: "primary.500",
    iconColor: "greyscale.100",
    textColor: "greyscale.100",
    closeButtonColor: "greyscale.100",
  },
} as const;

export function Alert({
  children,
  variant,
  isOpen,
  onClose,
  withIcon,
  isExperiment,
  ...props
}: PropsWithChildren<AlertProps>) {
  const styles = alertThemes[variant];

  if (!isOpen) return null;

  return (
    <ChakraAlert
      background={styles.background}
      borderBottom="1px"
      borderColor={styles.borderColor}
      {...props}
    >
      <Flex
        width="100%"
        margin="0 auto"
        justifyContent="space-between"
        alignItems={{ base: "left", md: "center" }}
        direction={{ base: "column", md: "row" }}
      >
        <Flex
          alignItems="center"
          mx="auto"
          w="full"
          maxW={{ base: "calc(100vw - 16px)", md: "768px", xl: "1200px" }}
        >
          {withIcon && <Info mr={3} width="20px" height="20px" color={styles.iconColor} />}
          <AlertTitle fontWeight="medium" color={styles.textColor}>
            {children}
          </AlertTitle>
          <Spacer />
          {onClose && (
            <CloseButton
              color={styles.closeButtonColor}
              position={isExperiment ? "absolute" : "static"}
              right={isExperiment ? 2 : -1}
              top={isExperiment ? 2 : -1}
              onClick={onClose}
              aria-label="Close notification"
              data-testid="returning-users-close-mobile"
            />
          )}
        </Flex>
      </Flex>
    </ChakraAlert>
  );
}
